import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { assertNever } from '@gitbook/util-ts';

export interface AskGitBookSearchSuggestionGroup {
    type: 'ask-gitbook';
}

export interface AskGitBookSearchSuggestion {
    type: 'ask-gitbook';
    key: string;
    query: string;
    groups: AskGitBookSearchSuggestionGroup[];
}

/**
 * Generate a Ask GitBook suggestion to include in search results when query looks like a question.
 */
export function useAskGitBookSuggestion(
    query: string,
    onSubmitAIQuery: (newQuery: string, trigger?: 'click_ask_gitbook' | 'press_enter') => void,
    hasAI?: boolean
) {
    const onOpenSuggestion = React.useCallback(
        (item: AskGitBookSearchSuggestion) => {
            switch (item.type) {
                case 'ask-gitbook': {
                    onSubmitAIQuery(item.query, 'click_ask_gitbook');
                    break;
                }
                default:
                    assertNever(item.type);
            }
        },
        [onSubmitAIQuery]
    );

    const onRenderSuggestion = React.useCallback((item: AskGitBookSearchSuggestion) => {
        switch (item.type) {
            case 'ask-gitbook':
                return {
                    title: item.query,
                    icon: Icon.SearchOrAsk,
                    action: 'Ask GitBook',
                    standout: true,
                };
            default:
                assertNever(item.type);
        }
    }, []);

    const suggestion = React.useMemo(() => {
        const searchQuery = query.trim();

        if (!hasAI || !isQuestion(query)) {
            return null;
        }

        return {
            type: 'ask-gitbook',
            key: `ask-gitbook:${searchQuery}`,
            query: searchQuery,
        };
    }, [query, hasAI]);

    return {
        suggestion,
        onOpenSuggestion,
        onRenderSuggestion,
    };
}

const questionWordsArray = [
    'who',
    'what',
    'where',
    'when',
    'why',
    'how',
    'explain',
    'is',
    'are',
    'was',
    'were',
    'do',
    'does',
    'did',
    'which',
    'whom',
    'whose',
    'can',
    'have',
    'give',
    'tell',
    'show',
    'find',
];

const questionWords = new Set(questionWordsArray);

function isQuestion(query: string): boolean {
    if (query.length > 25 || query.includes('?') || query.includes(' ')) {
        return true;
    }

    const words = query.toLowerCase().trim().split(/\s+/);

    if (words.length === 0) {
        return false;
    }

    for (const word of words) {
        if (questionWords.has(word)) {
            return true;
        }
    }

    return false;
}
